/**
 * useGovernancaStatus — hook para consultar o status de governança do
 * profissional logado (sub-etapa 6.2).
 *
 * Uso: chamado uma vez na Sidebar (topo da árvore autenticada). Cache local
 * de 60s para não sobrecarregar; refresh silencioso quando bloqueado.
 *
 * Não bloqueia nada por si só — apenas expõe `estaBloqueado` para outros
 * componentes decidirem. A partir da sub-etapa 6.3 os endpoints do backend
 * começam a retornar 423 e o interceptor toma ação.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

const CACHE_MS = 60 * 1000;

const DEFAULT_STATUS = {
  em_periodo_aviso: true,
  esta_bloqueado: false,
  data_inicio_enforcement: '2026-08-20',
  pendencias: {
    status_pendente_critico: 0,
    sem_evolucao_critico: 0,
    total_critico: 0,
    mais_antigo_dias: null,
  },
  perfil: null,
};

export const useGovernancaStatus = () => {
  const { authFetch, user } = useAuth();
  const [status, setStatus] = useState(DEFAULT_STATUS);
  const [loading, setLoading] = useState(false);
  const ultimaBusca = useRef(0);

  const carregar = useCallback(async (forcar = false) => {
    if (!user) return;
    const agora = Date.now();
    if (!forcar && agora - ultimaBusca.current < CACHE_MS) return;
    ultimaBusca.current = agora;
    setLoading(true);
    try {
      const res = await authFetch('/governanca/meu-status');
      setStatus(res.data || DEFAULT_STATUS);
    } catch (err) {
      // Silencioso — em caso de erro, mantém status default (não bloqueado)
      console.debug('useGovernancaStatus falhou:', err?.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, user]);

  useEffect(() => {
    carregar();
    // Refresh a cada 2 minutos enquanto a aba estiver aberta
    const id = setInterval(() => carregar(), 2 * 60 * 1000);
    return () => clearInterval(id);
  }, [carregar]);

  return { ...status, loading, recarregar: () => carregar(true) };
};

export default useGovernancaStatus;
